// Parent language is authoritative; dialect labels never override it.
const LANGUAGE_LOCALES = {
  english: "en-US", korean: "ko-KR", chinese: "zh-CN", mandarin: "zh-CN", cantonese: "zh-HK",
  japanese: "ja-JP", french: "fr-FR", german: "de-DE", spanish: "es-ES", portuguese: "pt-PT",
  italian: "it-IT", russian: "ru-RU", arabic: "ar", hindi: "hi-IN", bengali: "bn-BD",
  urdu: "ur-PK", persian: "fa-IR", farsi: "fa-IR", dari: "fa-AF", pashto: "ps-AF",
  marathi: "mr-IN", nepali: "ne-NP", tamil: "ta-IN", telugu: "te-IN", malayalam: "ml-IN",
  kannada: "kn-IN", punjabi: "pa-IN", gujarati: "gu-IN", sinhala: "si-LK",
  thai: "th-TH", vietnamese: "vi-VN", indonesian: "id-ID", malay: "ms-MY",
  filipino: "fil-PH", tagalog: "fil-PH", burmese: "my-MM", khmer: "km-KH", lao: "lo-LA",
  greek: "el-GR", turkish: "tr-TR", polish: "pl-PL", czech: "cs-CZ", slovak: "sk-SK",
  ukrainian: "uk-UA", romanian: "ro-RO", hungarian: "hu-HU", dutch: "nl-NL",
  swedish: "sv-SE", danish: "da-DK", norwegian: "nb-NO", finnish: "fi-FI",
  hebrew: "he-IL", swahili: "sw-KE", hausa: "ha-NG", "nigerian fulfulde": "ff-NG",
  yoruba: "yo-NG", igbo: "ig-NG", amharic: "am-ET", somali: "so-SO", zulu: "zu-ZA",
  afrikaans: "af-ZA", catalan: "ca-ES", basque: "eu-ES", galician: "gl-ES",
  welsh: "cy-GB", irish: "ga-IE", icelandic: "is-IS", lithuanian: "lt-LT",
  latvian: "lv-LV", estonian: "et-EE", serbian: "sr-RS", croatian: "hr-HR",
  bosnian: "bs-BA", bulgarian: "bg-BG", slovenian: "sl-SI", albanian: "sq-AL",
  armenian: "hy-AM", georgian: "ka-GE", azerbaijani: "az-AZ", kazakh: "kk-KZ",
  uzbek: "uz-UZ", mongolian: "mn-MN", tibetan: "bo-CN", uyghur: "ug-CN",
};

export function requireLanguage(language) {
  const value = String(language || "").trim();
  if (!value || /^(unknown|other|not_collected)$/i.test(value) || value.startsWith("+")) {
    throw new Error("Select a valid parent language before continuing.");
  }
  return value;
}

export function requireInferenceText(value) {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text || /AI[_ ]GENERATION[_ ]FAILED|AI generation failed|details redacted|translation service unavailable|^Error analyzing (audio|text)[.]?$|^Failed to (generate meaning|regenerate)[.]?$/i.test(text)
      || /Let's talk about (?:You are |User said:)/i.test(text)) {
    throw new Error("AI generation failed. Please retry; no research entry was submitted.");
  }
  return text;
}

export function parseInferenceResult(raw) {
  let result;
  try { result = typeof raw === "string" ? JSON.parse(raw) : raw; } catch (_) {
    throw new Error("The AI service returned an unverified legacy response. Please retry after the service is updated.");
  }
  if (!result || typeof result !== "object" || result.ok !== true) {
    throw new Error(typeof result?.error?.message === "string" ? result.error.message : "AI generation failed. Please retry.");
  }
  if (result.fallback === true || !result.model || !result.route) {
    throw new Error("The AI service did not confirm a successful model response.");
  }
  return { ...result, text: requireInferenceText(result.text) };
}

export async function requestInference(app, endpoint, args) {
  const response = await app.predict(endpoint, args);
  return parseInferenceResult(response?.data?.[0]);
}

export function getLanguageSpeechProfile(language, dialect = "") {
  const selected = String(language || "").trim().toLowerCase();
  let lang = LANGUAGE_LOCALES[selected];
  // No guessing from an ambiguous dialect or silently speaking an unknown language in English.
  if (!lang) return null;
  if (selected === "english") {
    if (/british/i.test(dialect)) lang = "en-GB";
    else if (/australian/i.test(dialect)) lang = "en-AU";
    else if (/indian/i.test(dialect)) lang = "en-IN";
    else if (/nigerian/i.test(dialect)) lang = "en-NG";
  }
  return { lang, rate: 0.96, pitch: 1 };
}

export function pickLanguageVoice(voices = [], language, dialect = "") {
  const profile = getLanguageSpeechProfile(language, dialect);
  if (!profile) return { profile: null, voice: null };
  const locale = profile.lang.toLowerCase();
  const primary = locale.split("-")[0];
  const voice = voices.find(item => String(item.lang).toLowerCase() === locale)
    || voices.find(item => String(item.lang).toLowerCase().split("-")[0] === primary);
  return { profile, voice: voice || null };
}
